import { Controller } from '@hotwired/stimulus';
import { createPopper } from '@popperjs/core';

export default class extends Controller {
    static targets = ["contextMenu", "alertTemplate", "table", "headers"];

    static values = {
        subscriptionGroupId: String,
        timezone: String,
        isMobile: Boolean
    };

    connect() {
        this.element.addEventListener('click', this.handleClick.bind(this));

        this.boundHideMenu = this.hideMenu.bind(this);
        document.addEventListener('click', this.boundHideMenu);

        if (this.hasTableTarget) {
            this.focusTodayColumn();
        }
    }

    disconnect() {
        document.removeEventListener('click', this.boundHideMenu);
        this.element.removeEventListener('click', this.handleClick);

        if (this.popperInstance) {
            this.popperInstance.destroy();
        }
    }

    handleClick(event) {
        let cell = null;

        if (this.isMobileValue) {
            cell = event.target.closest('.attendance-cell__mobile');
        } else {
            cell = event.target.closest('.attendance-cell');
        }

        if (cell) {
            this.showMenu(event, cell);
        } else if (event.target.closest('.context-menu li')) {
            this.setStatus(event);
        }
    }

    showMenu(event, cell) {
        event.preventDefault();
        event.stopPropagation();

        this.currentCell = cell;
        const contextMenu = this.contextMenuTarget;
        const statusCircle = cell.querySelector('.status-circle');

        document.querySelectorAll('.status-circle.selected').forEach(el => {
            el.classList.remove('selected');
        });

        statusCircle.classList.add('selected');

        contextMenu.style.display = 'block';

        if (this.popperInstance) {
            this.popperInstance.destroy();
        }

        this.popperInstance = createPopper(statusCircle, contextMenu, {
            placement: 'top',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 11],
                    },
                },
                {
                    name: 'preventOverflow',
                    options: {
                        boundary: this.element,
                    },
                },
                {
                    name: 'flip',
                    options: {
                        fallbackPlacements: ['bottom'],
                    },
                },
            ],
        });

        contextMenu.querySelectorAll('li').forEach(item => {
            item.style.display = 'flex';
        });
    }

    hideMenu(event) {
        const targetSelector = this.isMobileValue ?
            '.attendance-cell__mobile' :
            '.attendance-cell';

        if (event.target.closest('.context-menu') ||
            event.target.closest(targetSelector)) {
            return;
        }

        document.querySelectorAll('.status-circle.selected').forEach(el => {
            el.classList.remove('selected');
        });

        this.contextMenuTarget.style.display = 'none';
        if (this.popperInstance) {
            this.popperInstance.destroy();
            this.popperInstance = null;
        }
    }

    setStatus(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.currentCell) return;

        const cellDate = new Date(this.currentCell.dataset.date);
        const currentDate = this.getDateInTimezone(new Date(), this.timezoneValue);

        // set both dates to start of day for accurate comparison
        cellDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        if (cellDate > currentDate) {
            const alertText = this.alertTemplateTarget.innerHTML.trim();
            alert(alertText);
            return;
        }

        const listItem = event.target.closest('li');
        const status = listItem.dataset.status;

        this.currentCell.dataset.status = status;

        const circle = this.currentCell.querySelector('.status-circle');
        circle.className = 'status-circle ' + status;

        listItem.style.display = 'none';

        if (status === 'present') {
            this.addVisit(status);
        } else if (status === 'absent') {
            this.deleteVisit();
        }
    }

    async addVisit(status) {
        let startDate = `${this.currentCell.dataset.date} ${this.currentCell.dataset.time}`;

        try {
            const response = await fetch('/wp-json/api/dashboard/subscriptions/actions/add-visit', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    visitor_id: this.currentCell.dataset.visitorId,
                    contract_id: this.currentCell.dataset.contractId,
                    subscription_group_id: this.subscriptionGroupIdValue,
                    start_of_visitation: startDate,
                    status: status,
                }),
            });

            if (!response.ok) throw new Error(response.status);
            const data = await response.json();

            this.currentCell.dataset.visitId = data.visit_id;
        } catch (error) {}
    }

    async deleteVisit() {
        if (this.currentCell.dataset.visitId) {
            await fetch('/wp-json/api/dashboard/subscriptions/actions/delete-visit', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    visit_id: this.currentCell.dataset.visitId,
                }),
            })
                .then(() => delete this.currentCell.dataset.visitId)
                .catch((error) => console.error('Error:', error));
        }
    }

    getDateInTimezone(date, timezone) {
        return new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    }

    focusTodayColumn() {
        const today = this.getDateInTimezone(new Date(), this.timezoneValue).getUTCDate()

        const headers = this.headersTargets;

        let todayIndex = headers.findIndex(header => parseInt(header.dataset.date, 10) === today);

        if (todayIndex > -1) {
            const column = headers[todayIndex];

            const table = column.closest('table');
            const columnCells = table.querySelectorAll(`td:nth-child(${todayIndex + 2}), th:nth-child(${todayIndex + 2})`);

            columnCells.forEach(cell => {
                cell.style.backgroundColor = '#F0F9FF';
                cell.style.color = '#3a89b8';
            });

            column.scrollIntoView({ behavior: "smooth", inline: "center" });
        }
    }
}
